/**
 * Reset Position Tool Utilities
 *
 * Helper functions for the Reset Position control tool.
 * This tool animates all 6 body parts simultaneously to either
 * starting position or docking position.
 *
 * Features:
 * - Duration calculation based on movement speed
 * - Target position parameter (start/dock)
 * - Tool identification helpers
 */

import type { MotorPositions, MotorBodyPart } from '../stores/puppetStore';

/**
 * Reset Position tool ID constant
 */
export const RESET_POSITION_TOOL_ID = 'prebuilt-control-reset-position';

/**
 * Target position type for Reset Position tool
 */
export type ResetTargetPosition = 'start' | 'dock';

/**
 * Minimum duration for Reset Position animation (in seconds)
 * Prevents instant snap which looks jarring
 */
const MIN_RESET_DURATION = 0.5;

/**
 * All motor body parts for iteration
 */
const MOTOR_BODY_PARTS: MotorBodyPart[] = [
  'head',
  'body',
  'leftArm',
  'rightArm',
  'leftLeg',
  'rightLeg',
];

/**
 * Check if a tool ID is the Reset Position tool
 *
 * @param toolId - Tool ID to check
 * @returns true if this is the Reset Position tool
 */
export function isResetPositionTool(toolId: string): boolean {
  return toolId === RESET_POSITION_TOOL_ID;
}

/**
 * Calculate the duration for Reset Position animation
 *
 * Duration is based on the maximum distance any body part needs to travel,
 * divided by the movement speed. This ensures all body parts finish
 * their movement at the same time (synchronized animation).
 *
 * @param currentPositions - Current rotation of all body parts
 * @param targetPositions - Target rotation (starting or docking position)
 * @param movementSpeed - Movement speed in degrees per second
 * @returns Duration in seconds (minimum 0.5s)
 */
export function calculateResetDuration(
  currentPositions: MotorPositions,
  targetPositions: MotorPositions,
  movementSpeed: number
): number {
  // Calculate the maximum distance across all body parts
  let maxDistance = 0;

  for (const bodyPart of MOTOR_BODY_PARTS) {
    const current = currentPositions[bodyPart];
    const target = targetPositions[bodyPart];
    const distance = Math.abs(target - current);

    if (distance > maxDistance) {
      maxDistance = distance;
    }
  }

  // Calculate duration based on speed
  // duration = distance / speed
  const calculatedDuration = maxDistance / movementSpeed;

  // Enforce minimum duration to avoid instant snap
  return Math.max(MIN_RESET_DURATION, calculatedDuration);
}

/**
 * Get the target positions for a Reset Position action
 *
 * @param targetPosition - 'start' or 'dock'
 * @param startingPosition - Puppet's starting position config
 * @param dockingPosition - Puppet's docking position config
 * @returns Target motor positions
 */
export function getResetTargetPositions(
  targetPosition: ResetTargetPosition,
  startingPosition: MotorPositions,
  dockingPosition: MotorPositions
): MotorPositions {
  return targetPosition === 'start' ? startingPosition : dockingPosition;
}

/**
 * Default target position for new Reset Position tools
 */
export const DEFAULT_RESET_TARGET: ResetTargetPosition = 'start';
